import {
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { AlertCircle, Building, Loader2, Plus, UserPlus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { paths } from 'src/routes/paths'
import { Row } from 'src/shared/components/Semantic/all'
import { Alert, AlertDescription } from 'src/shared/components/ui/alert'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import { Switch } from 'src/shared/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { InviteCustomerDialog } from './components/InviteCustomerDialog'
import { useColumns } from './table/columns'
import { PageContainer } from 'src/shared/components/PageContainer'

const PAGE_SIZE = 20

export const OrganizationsPage = () => {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { usePaginatedOrganizations, createOrganization } = useOrgsApi({})

  const [search, setSearch] = useState('')
  const [hideAnon, setHideAnon] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const debouncedSearch = useDebounce(search, 300)

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  // Reset to first page when search or hide anon changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, hideAnon])

  const { data: paginatedData, isLoading } = usePaginatedOrganizations({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    excludeAnon: hideAnon,
    search: debouncedSearch || undefined,
    sortBy: sorting[0]?.id || 'created_at',
    sortDesc: sorting[0]?.desc ?? true,
  })

  const columns = useColumns()

  const data = useMemo(() => paginatedData?.data || [], [paginatedData?.data])

  const totalCount = paginatedData?.count ?? 0
  const pageCount = Math.ceil(totalCount / pagination.pageSize)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      pagination,
      columnVisibility,
      rowSelection,
    },
  })

  return (
    <PageContainer>
      <Row className="gap-2 items-center mb-4">
        <Building />
        <h1 className="text-2xl">Organizations</h1>

        <Input
          placeholder="Filter name..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-sm mx-4"
        />

        <Row className="gap-2 items-center ml-4">
          <Switch
            id="organizations-hide-anon"
            size="sm"
            checked={hideAnon}
            onCheckedChange={setHideAnon}
          />
          <label
            htmlFor="organizations-hide-anon"
            className="text-sm cursor-pointer text-muted-foreground"
          >
            {t('Hide non-authenticated', 'Dölj icke-autentiserade')}
          </label>
        </Row>

        <Button
          variant="primary"
          className="ml-auto"
          onClick={() => setInviteOpen(true)}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          {t('Invite customer', 'Bjud in kund')}
        </Button>
        <Button
          variant="secondary"
          onClick={async () => {
            const result = await createOrganization.mutateAsync({
              name: 'New org',
              source: 'internal',
            })
            navigate(`${paths.adminOrganizations}/${result?.data?.[0]?.id}`)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('Add organization', 'Lägg till organisation')}
        </Button>
      </Row>

      <InviteCustomerDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      <div
        className="border rounded-lg bg-white"
        style={{
          height: 'calc(100vh - 240px)',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24">
                  <Row className="gap-2 items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading...</span>
                  </Row>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24">
                  <Alert variant="default" className="mt-4 w-fit mx-auto">
                    <Row className="gap-2 items-center">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>Empty here dawg!</AlertDescription>
                    </Row>
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getSelectedRowModel().rows.length} of {totalCount} row(s)
          selected.
        </div>

        <div className="text-sm text-muted-foreground mr-4">
          Page {pagination.pageIndex + 1} of {pageCount || 1}
        </div>

        <div className="space-x-2">
          <Button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>

          <Button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </PageContainer>
  )
}
